/**
 * The CRM dashboards' built-in tabs (FB-29): one definition each, read by the
 * Admin presets and by the API when it first stores each seeded tab. The two
 * agree only at that first storage. A stored seed is an ordinary segment, so a
 * later change here (such as the Trial ending window, FB-28, Kate: "3 days")
 * updates the presets but not stored tabs; changing those is an ordinary edit
 * or a governed data change.
 */
export const CRM_TRIAL_ENDING_PERIOD = "next_3_days";

export type CrmBuiltInTabEntityType =
  "user" | "organisation" | "partner" | "contact";

export type CrmBuiltInTabFilter = {
  readonly category: string;
  readonly field: string;
  readonly condition: string;
  readonly value: string | readonly string[];
};

export type CrmBuiltInTab = {
  readonly entityType: CrmBuiltInTabEntityType;
  readonly label: string;
  readonly filters: readonly CrmBuiltInTabFilter[];
};

const trialEnding = (entityType: CrmBuiltInTabEntityType): CrmBuiltInTab => ({
  entityType,
  label: "Trial ending",
  filters: [
    {
      category: "timeline",
      field: "trial_ending_date",
      condition: "is_within",
      value: CRM_TRIAL_ENDING_PERIOD,
    },
  ],
});

/** Keyed by the seed key, which also fixes each seeded tab's id. */
export const CRM_BUILT_IN_TAB_FILTERS = {
  "users-trial": {
    entityType: "user",
    label: "Trial",
    filters: [
      {
        category: "organisation",
        field: "organisation_status",
        condition: "is",
        value: "trial",
      },
    ],
  },
  "users-trial-ending": trialEnding("user"),
  // Payment failed and Disconnected read each visible organisation's current
  // state (CRM parent PRD §2: any of the user's organisations).
  "users-payment-failed": {
    entityType: "user",
    label: "Payment failed",
    filters: [
      {
        category: "organisation",
        field: "payment_failed",
        condition: "is",
        value: "yes",
      },
    ],
  },
  "users-disconnected": {
    entityType: "user",
    label: "Disconnected",
    filters: [
      {
        category: "organisation",
        field: "provider_disconnected",
        condition: "is",
        value: "yes",
      },
    ],
  },
  "users-partner-clients": {
    entityType: "user",
    label: "Partner clients",
    filters: [
      {
        category: "user",
        field: "user_status",
        condition: "is",
        value: "partner_client_user",
      },
    ],
  },
  "organisations-xero-users": {
    entityType: "organisation",
    label: "Xero users",
    filters: [
      {
        category: "organisation",
        field: "provider",
        condition: "is",
        value: "xero",
      },
    ],
  },
  "organisations-trial-ending": trialEnding("organisation"),
  // Current state: an open payment recovery case, not a windowed "Has failed".
  "organisations-payment-failed": {
    entityType: "organisation",
    label: "Payment failed",
    filters: [
      {
        category: "billing",
        field: "payment_failed",
        condition: "is",
        value: "yes",
      },
    ],
  },
  "organisations-disconnected": {
    entityType: "organisation",
    label: "Disconnected",
    filters: [
      {
        category: "organisation",
        field: "provider_disconnected",
        condition: "is",
        value: "yes",
      },
    ],
  },
  "organisations-setup-incomplete": {
    entityType: "organisation",
    label: "Setup incomplete",
    filters: [
      {
        category: "organisation",
        field: "organisation_health",
        condition: "is_any_of",
        value: ["getting_started", "not_started"],
      },
    ],
  },
  "organisations-via-partner": {
    entityType: "organisation",
    label: "Via partner",
    filters: [
      {
        category: "partner",
        field: "via_partner",
        condition: "is",
        value: "yes",
      },
    ],
  },
  // DEC-05-R: a partner's live pending named invite to a member's login.
  "organisations-invited-by-partner": {
    entityType: "organisation",
    label: "Invited by partner",
    filters: [
      {
        category: "partner",
        field: "partner_invite_pending",
        condition: "is",
        value: "yes",
      },
    ],
  },
  "partners-trial-ending": trialEnding("partner"),
  // Current state: the partner's system Payment failed tag.
  "partners-payment-failed": {
    entityType: "partner",
    label: "Payment failed",
    filters: [
      {
        category: "partner",
        field: "payment_failed",
        condition: "is",
        value: "yes",
      },
    ],
  },
  "partners-needs-attention": {
    entityType: "partner",
    label: "Needs attention",
    filters: [
      {
        category: "client",
        field: "pending_client_invites",
        condition: "greater_than",
        value: "0",
      },
    ],
  },
  "partners-seats-unused": {
    entityType: "partner",
    label: "Seats unused",
    filters: [
      {
        category: "client",
        field: "remaining_seats",
        condition: "greater_than",
        value: "0",
      },
    ],
  },
  "partners-declining": {
    entityType: "partner",
    label: "Declining",
    filters: [
      {
        category: "partner",
        field: "partner_health",
        condition: "is",
        value: "declining",
      },
    ],
  },
  // Contacts delta row 21: New 90d and Warm. Partner and Users with subs
  // read linked user and organisation state, which a shared Contact segment
  // cannot hold (M39), so they are not seeded.
  "contacts-new90d": {
    entityType: "contact",
    label: "New 90d",
    filters: [
      {
        category: "timeline",
        field: "created_date",
        condition: "is_within",
        value: "last_90_days",
      },
    ],
  },
  "contacts-warm": {
    entityType: "contact",
    label: "Warm",
    filters: [
      {
        category: "contact",
        field: "contact_health",
        condition: "is",
        value: "warm",
      },
    ],
  },
} as const satisfies Record<string, CrmBuiltInTab>;

export type CrmBuiltInTabKey = keyof typeof CRM_BUILT_IN_TAB_FILTERS;
